/**
 * SSOT for vacation-package results → profile price audit:
 * sessionStorage keys derived from deal identifiers; fullDeal may expose a
 * different primary id than selectedResult in profile (numeric dealId vs blob id).
 */

const SNAPSHOT_KEY_PREFIX = "__HR_VP_PRICE_RESULTS_AUDIT_SNAPSHOT_v1__";

/** @param {Set<string>} set @param {unknown} v */
function __addAuditDealIdCandidate(set, v) {
  if (v == null) return;
  const s = String(v).trim();
  if (s !== "") set.add(s);
}

/**
 * All distinct id strings we might use for sessionStorage keys for the same logical package.
 * @param {Record<string, unknown> | null | undefined} dealLike
 * @returns {string[]}
 */
export function collectVpPriceAuditDealIdCandidates(dealLike) {
  const set = new Set();
  if (dealLike == null || typeof dealLike !== "object") {
    return [];
  }
  const nested =
    dealLike.fullDeal && typeof dealLike.fullDeal === "object"
      ? dealLike.fullDeal
      : null;
  const roots = nested ? [dealLike, nested] : [dealLike];

  for (const root of roots) {
    __addAuditDealIdCandidate(set, root.dealId);
    __addAuditDealIdCandidate(set, root.DealId);
    __addAuditDealIdCandidate(set, root.ID);
    __addAuditDealIdCandidate(set, root.id);
    __addAuditDealIdCandidate(set, root.ldsDealId);
    __addAuditDealIdCandidate(set, root.LdsDealId);
    __addAuditDealIdCandidate(set, dealLike?.ldsDealId);
    __addAuditDealIdCandidate(set, dealLike?.LdsDealId);
    __addAuditDealIdCandidate(set, root.disDealId);
    __addAuditDealIdCandidate(set, root.disdealid);
    __addAuditDealIdCandidate(set, root.disPackId);
    __addAuditDealIdCandidate(set, root.packageId);
    __addAuditDealIdCandidate(set, root.PackageId);
    __addAuditDealIdCandidate(set, root?.package?.packageId);
    __addAuditDealIdCandidate(set, root?.package?.PackageId);

    const lf = root.linkedFlights ?? dealLike.linkedFlights;
    if (Array.isArray(lf)) {
      for (const item of lf) {
        if (!item || typeof item !== "object") continue;
        __addAuditDealIdCandidate(set, item.ldsDealId);
        __addAuditDealIdCandidate(set, item.LdsDealId);
        __addAuditDealIdCandidate(set, item.dealId);
      }
    } else if (lf && typeof lf === "object") {
      __addAuditDealIdCandidate(set, lf.ldsDealId);
      __addAuditDealIdCandidate(set, lf.LdsDealId);
      __addAuditDealIdCandidate(set, lf.dealId);
    }
  }

  return Array.from(set);
}

/**
 * @param {Record<string, unknown> | null | undefined} dealLike fullDeal or selectedResult root
 * @returns {string | null}
 */
export function normalizePackageDealIdForAudit(dealLike) {
  if (dealLike == null || typeof dealLike !== "object") return null;
  const nested =
    dealLike.fullDeal && typeof dealLike.fullDeal === "object"
      ? dealLike.fullDeal
      : null;
  const root = nested || dealLike;
  const lf = root.linkedFlights ?? dealLike.linkedFlights;
  const ldsFromLf = Array.isArray(lf)
    ? lf[0]?.ldsDealId ?? lf[0]?.LdsDealId ?? null
    : lf?.ldsDealId ?? lf?.LdsDealId ?? null;
  const raw =
    root.dealId ??
    root.DealId ??
    root.ID ??
    root.id ??
    ldsFromLf ??
    root.ldsDealId ??
    root.LdsDealId ??
    null;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s !== "" ? s : null;
}

/**
 * Session key includes dealId; stable between results click and profile.
 * @param {string | number | null | undefined} dealId
 * @returns {string | null}
 */
export function vpPriceResultsSnapshotSessionStorageKey(dealId) {
  const n =
    dealId != null && String(dealId).trim() !== ""
      ? String(dealId).trim()
      : null;
  if (!n) return null;
  return `${SNAPSHOT_KEY_PREFIX}${n}`;
}

/**
 * @param {string | null} dealId normalized string id (single key)
 * @returns {{ sessionStorageKeyUsed: string | null, snapshot: object | null }}
 */
export function readVpPriceResultsSnapshotFromSession(dealId) {
  const sessionStorageKeyUsed = vpPriceResultsSnapshotSessionStorageKey(dealId);
  if (typeof window === "undefined" || !sessionStorageKeyUsed) {
    return { sessionStorageKeyUsed, snapshot: null };
  }
  try {
    const raw = window.sessionStorage.getItem(sessionStorageKeyUsed);
    if (!raw) return { sessionStorageKeyUsed, snapshot: null };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return { sessionStorageKeyUsed, snapshot: null };
    }
    return { sessionStorageKeyUsed, snapshot: parsed };
  } catch (_) {
    return { sessionStorageKeyUsed, snapshot: null };
  }
}

/**
 * Try every id variant (numeric dealId, blob id, ldsDealId, …) until a snapshot hits.
 * @param {Record<string, unknown> | null | undefined} dealLike selectedResult / fullDeal
 * @returns {{
 *   sessionStorageKeyUsed: string | null,
 *   snapshot: object | null,
 *   matchedStorageKey: string | null,
 *   keysTried: string[],
 * }}
 */
export function readVpPriceResultsSnapshotForDeal(dealLike) {
  const norm = normalizePackageDealIdForAudit(dealLike);
  const fromCollect = collectVpPriceAuditDealIdCandidates(dealLike);
  /** @type {string[]} */
  const ordered = [];
  const seen = new Set();
  const push = (id) => {
    if (id == null || String(id).trim() === "") return;
    const s = String(id).trim();
    if (seen.has(s)) return;
    seen.add(s);
    ordered.push(s);
  };
  if (norm) push(norm);
  for (const id of fromCollect) push(id);

  /** @type {string[]} */
  const keysTried = [];
  const primaryKey = vpPriceResultsSnapshotSessionStorageKey(norm ?? ordered[0] ?? null);

  if (typeof window === "undefined") {
    return {
      sessionStorageKeyUsed: primaryKey,
      snapshot: null,
      matchedStorageKey: null,
      keysTried,
    };
  }

  for (const id of ordered) {
    const key = vpPriceResultsSnapshotSessionStorageKey(id);
    if (!key) continue;
    keysTried.push(key);
    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed === "object" &&
        Object.keys(parsed).length > 0
      ) {
        return {
          sessionStorageKeyUsed: key,
          snapshot: parsed,
          matchedStorageKey: key,
          keysTried,
        };
      }
    } catch (_) {
      /* ignore */
    }
  }

  return {
    sessionStorageKeyUsed: primaryKey,
    snapshot: null,
    matchedStorageKey: null,
    keysTried,
  };
}

/**
 * Write the same snapshot under every candidate id key so profile (different id shape) still finds it.
 * @param {Record<string, unknown> | null | undefined} dealLikeForKeys fullDeal from results card
 * @param {Record<string, unknown>} snapshotPayload audit row
 * @returns {{ keysWritten: string[], primaryKeyUsed: string | null }}
 */
export function writeVpPriceResultsSnapshotToSession(dealLikeForKeys, snapshotPayload) {
  /** @type {{ keysWritten: string[], primaryKeyUsed: string | null }} */
  const out = { keysWritten: [], primaryKeyUsed: null };
  if (typeof window === "undefined") {
    return out;
  }
  const payload =
    snapshotPayload != null && typeof snapshotPayload === "object"
      ? snapshotPayload
      : dealLikeForKeys;
  const forKeys =
    snapshotPayload != null &&
    dealLikeForKeys != null &&
    typeof dealLikeForKeys === "object"
      ? dealLikeForKeys
      : payload;

  if (!payload || typeof payload !== "object") {
    return out;
  }

  const candidateSet = new Set([
    ...collectVpPriceAuditDealIdCandidates(forKeys),
    ...collectVpPriceAuditDealIdCandidates(payload),
  ]);
  const norm = normalizePackageDealIdForAudit(forKeys);
  if (norm) candidateSet.add(norm);
  const normPayload = normalizePackageDealIdForAudit(payload);
  if (normPayload) candidateSet.add(normPayload);
  if (payload.dealId != null) candidateSet.add(String(payload.dealId).trim());

  const canonicalDealId = norm ?? normPayload ?? payload.dealId ?? null;
  const bodyObj = {
    ...payload,
    dealId:
      canonicalDealId != null
        ? String(canonicalDealId)
        : payload.dealId != null
          ? String(payload.dealId)
          : null,
    _vpAuditIdCandidates: Array.from(candidateSet),
  };
  const body = JSON.stringify(bodyObj);

  for (const id of candidateSet) {
    const key = vpPriceResultsSnapshotSessionStorageKey(id);
    if (!key) continue;
    try {
      window.sessionStorage.setItem(key, body);
      out.keysWritten.push(key);
    } catch (_) {
      /* quota / private mode */
    }
  }

  out.primaryKeyUsed = vpPriceResultsSnapshotSessionStorageKey(
    canonicalDealId ?? Array.from(candidateSet)[0] ?? null
  );
  return out;
}

/**
 * Align with PaymentSummary / hero: CC+fee → with card fee → commission.
 * @returns {{ value: number | null, path: string }}
 */
export function resolveOurPriceDisplayIlsForAudit(ourPrice) {
  if (!ourPrice || typeof ourPrice !== "object") {
    return { value: null, path: "(no ourPrice)" };
  }
  const cc = Number(ourPrice.priceWithCommissionAndCreditCardFee);
  if (Number.isFinite(cc) && cc > 0) {
    return { value: cc, path: "ourPrice.priceWithCommissionAndCreditCardFee" };
  }
  const card = Number(ourPrice.priceWithCreditCardFee);
  if (Number.isFinite(card) && card > 0) {
    return { value: card, path: "ourPrice.priceWithCreditCardFee" };
  }
  const comm = Number(ourPrice.priceWithCommission);
  if (Number.isFinite(comm) && comm > 0) {
    return { value: comm, path: "ourPrice.priceWithCommission" };
  }
  return { value: null, path: "(ourPrice not ready)" };
}
